import React from 'react';

/**
 * The overall result's ring — Figma node 16370:191303.
 *
 * Not `RingGauge`, the course page's 108px ring: this one is the report's own,
 * and its geometry is read off the file's two exports rather than scaled from
 * that one — a 180px box, a gray/100 track of r 83 and 14 wide, and an arc on the
 * same circle with round ends. The file exports the arc as a filled 92% path; it
 * is drawn here as a stroke so it can be any value.
 *
 * The percentage and the word under it take the arc's colour: green/420 for a
 * pass, as the frame draws it, and the "غير نشط" red for a fail, which the frame
 * does not draw.
 *
 * It starts at twelve o'clock and runs clockwise in both languages, like the
 * course page's ring: a clock face does not mirror.
 */
const RADIUS = 83;
const WIDTH = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const TONES = {
    pass: { arc: 'tw-stroke-v2-student-pass', text: 'tw-text-v2-student-pass' },
    fail: { arc: 'tw-stroke-v2-student-danger', text: 'tw-text-v2-student-danger' },
};

const ScoreRing = ({ value, caption, tone = 'pass', label }) => {
    const percent = Math.min(100, Math.max(0, Math.round(Number(value) || 0)));
    const colours = TONES[tone] ?? TONES.pass;

    return (
        <div role="img" aria-label={label} className="tw-relative tw-size-[180px] tw-shrink-0">
            <svg width="180" height="180" viewBox="0 0 180 180" aria-hidden className="tw-block tw--rotate-90">
                <circle cx="90" cy="90" r={RADIUS} fill="none" strokeWidth={WIDTH} className="tw-stroke-v2-neutral-100" />
                {/* A round cap at 0% would still paint a dot, so an empty ring draws no arc. */}
                {percent > 0 && (
                    <circle
                        cx="90"
                        cy="90"
                        r={RADIUS}
                        fill="none"
                        strokeWidth={WIDTH}
                        strokeLinecap="round"
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={CIRCUMFERENCE * (1 - percent / 100)}
                        className={`${colours.arc} tw-transition-[stroke-dashoffset] tw-duration-700`}
                    />
                )}
            </svg>

            <div aria-hidden className={`tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[4px] tw-text-center ${colours.text}`}>
                <span className="tw-text-[32px] tw-font-bold tw-leading-[1.4]">{percent}%</span>
                <span className="tw-text-[14px] tw-leading-[1.6]">{caption}</span>
            </div>
        </div>
    );
};

export default ScoreRing;
